import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  InputAdornment,
  Alert
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  ContactEmergency as EmergencyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface LandlordInfoStepProps {
  stepProgress: number;
}

const LandlordInfoStep: React.FC<LandlordInfoStepProps> = ({ stepProgress }) => {
  const { control } = useFormContext();
  
  // Use the progress passed from the main LeaseForm component
  const progress = stepProgress;

  return (
    <StepFormLayout
      title="Landlord Information"
      description="Essential contact details for the property owner or manager"
      icon={<PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={progress}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>💡 Tip:</strong> This information will appear on the official lease document. Ensure all details are accurate and up-to-date.
        </Typography>
      </Alert>
      
      {/* Main Contact Information */}
      <FormSection
        title="Primary Contact"
        icon={<PersonIcon color="primary" />}
        isRequired={true}
        defaultExpanded={true}
      >
        <Stack spacing={3}>
          <FormTextField
            name="landlord.fullName"
            control={control}
            label="Full Legal Name"
            placeholder="e.g., John Smith or ABC Property Management LLC"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 3 
          }}>
            <FormTextField
              name="landlord.email"
              control={control}
              label="Email Address"
              type="email"
              placeholder="landlord@example.com"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField
              name="landlord.phone"
              control={control}
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>
      </FormSection>
      
      {/* Mailing Address */}
      <FormSection
        title="Mailing Address"
        icon={<HomeIcon color="primary" />}
        tooltip="Where legal notices and correspondence should be sent"
        isRequired={true}
        defaultExpanded={false}
      >
        <Stack spacing={3}>
          <FormTextField
            name="landlord.address.street"
            control={control}
            label="Street Address"
            placeholder="123 Main Street, Apt 2B"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HomeIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, 
            gap: 3 
          }}>
            <FormTextField
              name="landlord.address.city"
              control={control}
              label="City"
              placeholder="Los Angeles"
              required
            />
            <FormTextField
              name="landlord.address.state"
              control={control}
              label="State"
              placeholder="CA"
              required
              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            />
            <FormTextField
              name="landlord.address.zip"
              control={control}
              label="ZIP Code"
              placeholder="90210"
              required
              inputProps={{ maxLength: 10 }}
            />
          </Box>
        </Stack>
      </FormSection>
      
      {/* Optional Information */}
      <FormSection
        title="Additional Information"
        icon={<BusinessIcon color="primary" />}
        tooltip="Management company and emergency contact details"
        isRequired={false}
        defaultExpanded={false}
      >
          <Stack spacing={3}>
            <FormTextField 
              name="landlord.managementCompany" 
              control={control} 
              label="Management Company" 
              placeholder="e.g., ABC Property Management"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormTextField 
              name="landlord.emergencyContact" 
              control={control} 
              label="Emergency Contact" 
              placeholder="Name and phone for urgent property issues"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmergencyIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
      </FormSection>
      

      </Box>
    </StepFormLayout>
  );
};

export default LandlordInfoStep; 